import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import MetricCard from '../Common/MetricCard';
import { Chat, Construction, Star, Event, Percent } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { analyticsService, RealtimeDashboardResponse, DashboardSummary } from '../../services/analyticsService';
import { projectService } from '../../services/projectService';
import { schedulingService } from '../../services/schedulingService';
import { useAppMeta } from '../../contexts/AppMetaContext';

type ProjectsOverview = {
  total_projects: number;
  active_projects: number;
  inactive_projects: number;
  total_contexts: number;
  total_documents: number;
  projects?: { project_code: string; context_count: number; document_count: number; last_updated?: string }[];
  generated_at: string;
};

type SchedulingDashboardSummary = {
  today_meetings_count: number;
  summary_date?: string;
};

type AnalyticsSummary = {
  conversion_rate_today?: number;
  conversion_rate_change?: number;
};

const Dashboard: React.FC = () => {
  const { chatbotName } = useAppMeta();
  const [realtime, setRealtime] = useState<RealtimeDashboardResponse | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [projectsOverview, setProjectsOverview] = useState<ProjectsOverview | null>(null);
  const [schedulingSummary, setSchedulingSummary] = useState<SchedulingDashboardSummary | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
        const results = await Promise.allSettled([
          projectService.getProjectsOverview(),
          schedulingService.getDashboardSummary(),
          analyticsService.getRealtimeDashboard(),
          analyticsService.getDashboardSummary(),
          projectService.getInventoryStatus(),
          projectService.getProjects(0, 1000, true),
        ]);

      if (cancelled) return;

      const [projectsResult, schedulingResult, realtimeResult, dashboardSummaryResult, inventoryResult, projectsListResult] = results;

      if (realtimeResult.status === 'fulfilled') {
        setRealtime(realtimeResult.value);
      } else {
        console.error('Failed to load realtime dashboard metrics:', realtimeResult.reason);
      }

      if (dashboardSummaryResult.status === 'fulfilled') {
        setDashboardSummary(dashboardSummaryResult.value);
      } else {
        console.error('Failed to load dashboard summary:', dashboardSummaryResult.reason);
      }

      if (projectsResult.status === 'fulfilled') {
        setProjectsOverview(projectsResult.value);
      } else {
        console.error('Failed to load projects overview:', projectsResult.reason);
      }

      if (schedulingResult.status === 'fulfilled') {
        setSchedulingSummary(schedulingResult.value);
      } else {
        console.error('Failed to load scheduling dashboard summary:', schedulingResult.reason);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const navigate = useNavigate();

  // Additional project metrics derived from fetched endpoints
  const [projectsWithAvailableUnits, setProjectsWithAvailableUnits] = useState<number | null>(null);
  const [projectsWithUrl, setProjectsWithUrl] = useState<number | null>(null);
  const [projectsWithBrochure, setProjectsWithBrochure] = useState<number | null>(null);

  useEffect(() => {
    // derive the additional project metrics whenever projectsOverview or inventory/project list responses are available
    const derive = async () => {
      try {
        // fetch projects list first
        const projectsListResp = await projectService.getProjects(0, 1000, true);
        const projectsArr = projectsListResp.projects || [];

        // attempt to fetch inventory status; if unavailable (404) fall back to deriving availability from projectsArr
        let inventory: { project_code: string; available_units?: number; availability_status?: string }[] = [];
        try {
          inventory = await projectService.getInventoryStatus();
        } catch (invErr: any) {
          // Do not fallback: if inventory endpoint is not present, leave counts empty and surface the error
          console.error('inventory-status endpoint unavailable; project availability metrics will be unavailable', invErr?.message || invErr);
          setProjectsWithAvailableUnits(null);
          setProjectsWithUrl(null);
          setProjectsWithBrochure(null);
          return;
        }

        const availableProjectCodes = new Set(inventory.filter(p => (p.available_units ?? 0) > 0).map(p => p.project_code));
        const availableCount = availableProjectCodes.size;
        setProjectsWithAvailableUnits(availableCount);

        // count projects with link/pdf only among available projects
        const withUrlCount = projectsArr.filter(p => !!(p.link) && availableProjectCodes.has(p.project_code)).length;
        const withBrochureCount = projectsArr.filter(p => !!(p.pdf_link) && availableProjectCodes.has(p.project_code)).length;
        setProjectsWithUrl(withUrlCount);
        setProjectsWithBrochure(withBrochureCount);
      } catch (err) {
        console.error('Error deriving project metrics:', err);
      }
    };

    derive();
  }, [projectsOverview]);

  const activeConversations: number | null =
    realtime?.system_status?.active_conversations ??
    null;

  const activeProjects: number | null = projectsOverview?.active_projects ?? null;
  const scheduledMeetingsToday: number | null = schedulingSummary?.today_meetings_count ?? null;
  const userSatisfaction: number | null = realtime?.dashboard_summary?.user_satisfaction ?? null;
  const conversionRateToday: number | null = dashboardSummary?.conversion_rate_today ?? null;
  const conversionRateChange: number = (dashboardSummary as any)?.conversion_rate_change ?? 0;

  // Placeholder trend/change values: use realtime data when available, otherwise show 0 as placeholder
  const activeConversationsChange: number = (realtime?.dashboard_summary as any)?.active_conversations_change ?? 0;
  // Projects overview does not provide a change metric yet; use placeholder 0
  const activeProjectsChange: number = 0;
  const scheduledMeetingsChange: number = 0;
  const userSatisfactionChange: number = (realtime?.dashboard_summary as any)?.user_satisfaction_change ?? 0;

  return (
    <Box className="fade-in">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard Principal
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Bienvenido al panel de administración de {chatbotName}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Conversaciones Activas"
            value={activeConversations ?? '—'}
            change={activeConversationsChange}
            icon={<Chat />}
            color="#4caf50"
            onClick={() => navigate('/conversations')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Citas Programadas"
            value={scheduledMeetingsToday ?? '—'}
            change={scheduledMeetingsChange}
            icon={<Event />}
            color="#ff9800"
            onClick={() => navigate('/scheduling')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Tasa de Conversión"
            value={conversionRateToday != null ? conversionRateToday : '—'}
            change={conversionRateChange}
            icon={<Percent />}
            color="#f85d5d"
            format="percentage"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Satisfacción Cliente"
            value={userSatisfaction != null ? userSatisfaction.toFixed(1) : '—'}
            change={userSatisfactionChange}
            icon={<Star />}
            color="#9c27b0"
            format="rating"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Proyectos en Curso"
            value={projectsWithAvailableUnits ?? '—'}
            change={activeProjectsChange}
            icon={<Construction />}
            color="#2196f3"
            details={[
              { label: 'Con URL', value: projectsWithUrl ?? '—' },
              { label: 'Con Brochure', value: projectsWithBrochure ?? '—' },
            ]}
            onClick={() => navigate('/projects')}
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actividad Reciente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Los datos de actividad se mostrarán aquí una vez implementada la funcionalidad completa.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
